import type { AnyClock } from "@repo/lib/clock";
import { ObjectKeyHasher } from "@repo/lib/keyHasher";
import {
  RepoCache,
  type GetObservableOptions,
} from "@repo/shared-client/repoCache";
import {
  CreateTeamDtoSchema,
  PatchTeamDtoSchema,
  type RoleType,
  type Team,
  type TeamMember,
  type TeamMemberWContact,
  type TeamWContact,
  type User,
} from "@repo/types";
import { cacheDefaults, hookPriorities } from "./common";
import { apiService } from "@/services/apiService";
import { firstValueFrom, of, type Observable } from "rxjs";
import { nullObservable } from "@repo/shared-client/commonObservables";
import type { OnlyRequire } from "@repo/lib/types";
import { roleAllowed, userStore } from "@/stores/userStore";
import { contactInfoRepo } from "./contactInfoRepo";
import { userRepo } from "./userRepo";
import { RepoMutationHooks } from "./repoMutationHooks";
import { baseRepo } from "./baseRepo";

type TeamGetKey = { type: "get"; tenantId: string; teamId: string };
type TeamListKey = { type: "list"; tenantId: string };
type TeamKey = TeamGetKey | TeamListKey;

export type TeamDto = Team & {
  tenantId: string;
};

export type TeamWContactDto = TeamWContact & {
  tenantId: string;
};

export type TeamIdPath = { tenantId: string; teamId: string | null };

export class TeamRepository {
  private cache: RepoCache<string, TeamWContactDto, ObjectKeyHasher, AnyClock>;
  private hooks = new RepoMutationHooks<
    TeamIdPath,
    TeamDto | TeamWContactDto
  >();

  // expose hook registry
  addHook = this.hooks.addHook;
  removeHook = this.hooks.removeHook;

  publishNewer(team: TeamWContactDto) {
    this.cache.publish(team.id, team);
  }

  teamToDto(tenantId: string, team: TeamWContact): TeamWContactDto;
  teamToDto(tenantId: string, team?: null): null;
  teamToDto(
    tenantId: string,
    team?: TeamWContact | null
  ): TeamWContactDto | null;
  teamToDto(
    tenantId: string,
    team?: TeamWContact | null
  ): TeamWContactDto | null {
    return team ? { ...team, tenantId } : null;
  }

  constructor() {
    this.cache = new RepoCache({
      requestKeyHasher: new ObjectKeyHasher({ sortKeys: false }),
      expiresAfterSeconds: cacheDefaults.expiresAfterSeconds,
      purgeExpirationAfterSeconds: cacheDefaults.purgeExpiredAfterSeconds,
      fetch: async ({ requestKey, publish }) => {
        const key = requestKey as TeamKey;

        switch (key.type) {
          case "get": {
            const { tenantId, teamId } = key;
            const team = (await apiService.v1.getTeam(tenantId, teamId)).data;
            const users = await firstValueFrom(userRepo.list(tenantId));

            // only publish if it's the right tenant
            const prev = this.cache.table.get(teamId);
            if (!prev || prev.tenantId === tenantId) {
              publish(
                key.teamId,
                this.teamToDto(tenantId, await this.addContactInfo(team, users))
              );
            }
            break;
          }
          case "list": {
            const { tenantId } = key;
            const teams = (await apiService.v1.listTeams(tenantId)).data;
            const users = await firstValueFrom(userRepo.list(tenantId));
            const teamsWContact = await Promise.all(
              teams.map((team) => this.addContactInfo(team, users))
            );
            for (const team of teamsWContact) {
              publish(team.id, this.teamToDto(tenantId, team));
            }
            break;
          }
        }
      },
      collect: ({ requestKey, table }) => {
        const key = requestKey as TeamKey;

        switch (key.type) {
          case "get": {
            return table.get(key.teamId) ?? null;
          }
          case "list": {
            const teams: TeamWContactDto[] = [];
            for (const team of table.values()) {
              if (team.tenantId === key.tenantId) {
                teams.push(team);
              }
            }
            return teams;
          }
        }
      },
      affectedKeys: ({ entryData, affectsKey }) => {
        // extract
        const { tenantId, id } = entryData;
        // affect
        affectsKey(this.getKey(tenantId, id));
        affectsKey(this.listKey(tenantId));
      },
    });

    baseRepo.addRepo(this);
  }

  private getKey(tenantId: string, teamId: string): TeamGetKey {
    return { type: "get", tenantId, teamId };
  }
  private listKey(tenantId: string): TeamListKey {
    return { type: "list", tenantId };
  }

  get(
    tenantId: string,
    teamId: string,
    options?: GetObservableOptions
  ): Observable<TeamWContactDto | null> {
    if (!tenantId || !teamId) {
      return nullObservable;
    }
    const key = this.getKey(tenantId, teamId);
    return this.cache.observable(key, options);
  }
  getCached(tenantId: string, teamId: string): TeamWContactDto | null {
    const key = this.getKey(tenantId, teamId);
    return this.cache.collectIfUnexpired(key, null);
  }

  list(
    tenantId: string,
    options?: GetObservableOptions
  ): Observable<TeamWContactDto[]> {
    if (!tenantId) {
      return of([]);
    }
    const key = this.listKey(tenantId);
    return this.cache.observable(key, options);
  }
  listCached(tenantId: string): TeamWContactDto[] | null {
    const key = this.listKey(tenantId);
    return this.cache.collectIfUnexpired(key, null);
  }

  async create(
    tenantId: string,
    data: Partial<TeamDto | TeamWContactDto>
  ): Promise<TeamWContactDto> {
    if (!tenantId) {
      throw new Error("Failed to create team: tenantId is blank.");
    }

    const hooks = this.hooks.execute({ tenantId, teamId: null }, "create");
    data = await hooks.before(data);

    const rawCreated = (
      await apiService.v1.createTeam(
        tenantId,
        CreateTeamDtoSchema.parse({ name: "", ...data })
      )
    ).data;

    const users = await firstValueFrom(userRepo.list(tenantId));

    let created = this.teamToDto(
      tenantId,
      await this.addContactInfo(rawCreated, users)
    );

    created = (await hooks.after(created)) as TeamWContactDto;

    // publish new team
    this.cache.publish(created.id, created);

    return created;
  }

  async update(
    tenantId: string,
    teamId: string,
    data: Partial<TeamDto | TeamWContactDto>
  ): Promise<TeamWContactDto> {
    if (!tenantId || !teamId) {
      throw new Error("Failed to update team: tenantId or teamId is blank.");
    }

    const hooks = this.hooks.execute({ tenantId, teamId }, "update");
    data = await hooks.before(data);

    const rawUpdated = (
      await apiService.v1.updateTeam(
        tenantId,
        teamId,
        PatchTeamDtoSchema.parse(data)
      )
    ).data;

    const users = await firstValueFrom(userRepo.list(tenantId));

    let updated = this.teamToDto(
      tenantId,
      await this.addContactInfo(rawUpdated, users)
    );

    updated = (await hooks.after(updated)) as TeamWContactDto;

    // publish updated team
    this.cache.publish(updated.id, updated);

    return updated;
  }

  async delete(tenantId: string, teamId: string): Promise<void> {
    if (!tenantId || !teamId) {
      throw new Error("Failed to delete team: tenantId or teamId is blank.");
    }

    const hooks = this.hooks.execute({ tenantId, teamId }, "delete");
    await hooks.before(null);

    await apiService.v1.deleteTeam(tenantId, teamId);

    await hooks.after(null);
    this.cache.publish(teamId, null);
  }

  invalidate(team: OnlyRequire<TeamDto, "id">) {
    this.cache.invalidateEntry(team.id);
  }

  invalidateAll() {
    this.cache.clear();
  }

  /** Pull contact info from an associated user, and inject into a team member. */
  async addMemberContactInfo(
    teamMember: TeamMember,
    userMap?: Partial<Record<string, User>>
  ): Promise<TeamMemberWContact> {
    return {
      ...teamMember,
      contactInfo: contactInfoRepo.extractFromUser(
        userMap?.[teamMember.userId] ??
          (await firstValueFrom(userRepo.get(teamMember.userId)))
      ),
    };
  }

  /** Remove contact info from a team member. */
  removeMemberContactInfo(teamMember: TeamMemberWContact): TeamMember {
    const { contactInfo: _, ...member } = teamMember;
    return member;
  }

  /** Pull contact info from associated users and inject into members. */
  async addContactInfo(team: Team, users?: User[]): Promise<TeamWContact>;
  async addContactInfo(team?: null, users?: User[]): Promise<null>;
  async addContactInfo(
    team?: Team | null,
    users?: User[]
  ): Promise<TeamWContact | null>;
  async addContactInfo(
    team?: Team | null,
    users: User[] = []
  ): Promise<TeamWContact | null> {
    if (!team) return null;

    const userMap = users.reduce(
      (map, user) => ((map[user.id] = user), map),
      Object.create(null) as Partial<Record<string, User>>
    );

    return {
      ...team,
      teamMembers: await Promise.all(
        team.teamMembers?.map((member) =>
          this.addMemberContactInfo(member, userMap)
        )
      ),
    };
  }

  /** Remove contact info from team members before upload. */
  removeContactInfo(team: TeamDto | TeamWContact): Team;
  removeContactInfo(team: Partial<TeamDto | TeamWContactDto>): Partial<TeamDto>;
  removeContactInfo(team?: null): null;
  removeContactInfo(team?: TeamDto | TeamWContactDto | null): TeamDto | null;
  removeContactInfo(
    team?: Partial<TeamDto | TeamWContactDto> | null
  ): Partial<TeamDto> | null;
  removeContactInfo(
    team?: Partial<TeamDto | TeamWContactDto> | null
  ): Partial<TeamDto> | null {
    if (!team?.teamMembers?.length) return team ?? null;
    return {
      ...team,
      teamMembers: (team.teamMembers as TeamMemberWContact[]).map((member) =>
        this.removeMemberContactInfo(member)
      ),
    };
  }

  /** Add or replace a team member on the team. Force-reloads and saves the team, to ensure it
   * is updated with the latest data. */
  async addUpdateMember(
    tenantId: string,
    teamId: string,
    newMember: TeamMember | TeamMemberWContact
  ): Promise<TeamWContactDto> {
    const team = await firstValueFrom(
      this.get(tenantId, teamId, { forceReload: true })
    );
    if (!team) throw new Error("addUpdateMember: Team not found.");

    newMember = await this.addMemberContactInfo(newMember);

    const { teamMembers } = team;

    const existingIndex = teamMembers.findIndex(
      (member) => member.userId === newMember.userId
    );
    if (existingIndex > -1) {
      teamMembers[existingIndex] = newMember as TeamMemberWContact;
    } else {
      teamMembers.push(newMember as TeamMemberWContact);
    }

    const { userId } = newMember;

    const addRemoveUserId = (array: string[], role: RoleType) => {
      const i = array.indexOf(userId);
      if (newMember.role === role) {
        if (i < 0) array.push(userId);
      } else {
        if (i > -1) array.splice(i, 1);
      }
    };

    const { allUsers } = team;
    let { adminUsers, editUsers, submitUsers, viewUsers, noneUsers } = team;

    if (!allUsers.includes(userId)) allUsers.push(userId);

    addRemoveUserId((adminUsers ??= []), "admin");
    addRemoveUserId((editUsers ??= []), "edit");
    addRemoveUserId((submitUsers ??= []), "submit");
    addRemoveUserId((viewUsers ??= []), "view");
    addRemoveUserId((noneUsers ??= []), "none");

    return await this.update(tenantId, teamId, {
      teamMembers,
      allUsers,
      adminUsers,
      editUsers,
      submitUsers,
      viewUsers,
      noneUsers,
    });
  }

  /** Remove a team member from the team. Force-reloads and saves the team, to ensure it is
   * updated with the latest data. */
  async removeMember(
    tenantId: string,
    teamId: string,
    memberUserId: string
  ): Promise<TeamWContactDto> {
    const team = await firstValueFrom(
      this.get(tenantId, teamId, { forceReload: true })
    );
    if (!team) throw new Error("removeMember: Team not found.");

    const { teamMembers } = team;

    const existingIndex = teamMembers.findIndex(
      (member) => member.userId === memberUserId
    );
    if (existingIndex > -1) {
      teamMembers.splice(existingIndex, 1);
    }

    const removeUserId = (array: string[]) => {
      const i = array.indexOf(memberUserId);
      if (i > -1) array.splice(i, 1);
    };

    let { allUsers, adminUsers, editUsers, submitUsers, viewUsers, noneUsers } =
      team;

    removeUserId((allUsers ??= []));
    removeUserId((adminUsers ??= []));
    removeUserId((editUsers ??= []));
    removeUserId((submitUsers ??= []));
    removeUserId((viewUsers ??= []));
    removeUserId((noneUsers ??= []));

    return await this.update(tenantId, teamId, {
      teamMembers,
      allUsers,
      adminUsers,
      editUsers,
      submitUsers,
      viewUsers,
      noneUsers,
    });
  }

  canViewConfidentialDocuments(
    userId?: string | null,
    team?: Team | null,
    /** If the actively selected team / role should be used for determining the grant. */
    activePreview: boolean = false
  ): boolean {
    // if data is missing, fail
    if (!userId || !team) return false;

    let sameTeam = false;
    let role: RoleType | null = null;
    if (activePreview) {
      const user = userStore.getState();
      sameTeam = user.activeTeam !== "org" && user.activeTeam?.id === team.id;
      role = user.activeRole ?? null;
    }

    // if user is a voting member of the team, or if they have at least edit authority, succeed
    return team.teamMembers.some((member) => {
      if (member.userId !== userId) return false;
      if (member.class === "voting") return true;

      if (activePreview && (!sameTeam || !role)) return false;

      return roleAllowed({
        current: activePreview ? role! : member.role,
        required: "edit",
      });
    });
  }

  init(data?: Partial<TeamDto>): TeamDto {
    return baseRepo.init({
      tenantId: baseRepo.getTenantId() || "UNKNOWN",
      name: "",
      adminUsers: [],
      editUsers: [],
      submitUsers: [],
      viewUsers: [],
      noneUsers: [],
      allUsers: [],
      teamMembers: [],
      ...data,
    });
  }
}

export const teamRepo = new TeamRepository();

teamRepo.addHook(() => {
  const before = (team: Partial<TeamDto | TeamWContactDto>) => {
    return teamRepo.removeContactInfo(team);
  };

  return {
    beforeCreate: before,
    beforeUpdate: before,
  };
}, hookPriorities.removeNestedEntities);
